(() => {
  function createQueue() {
    return {
      /**
       * @type {Array<Function>}
       * @private
       */
      queue: [],

      /**
       * @type {boolean}
       * @private
       */
      isProcessing: false,

      /**
       * Adds an asynchronous function to the queue.
       * If the queue is not currently processing, it starts processing immediately.
       *
       * @param {Function} asyncFunc - The asynchronous function to be added to the queue.
       * @returns {void}
       */
      addToQueue(asyncFunc) {
        this.queue.push(asyncFunc);
        if (!this.isProcessing) {
          this.processQueue();
        }
      },

      /**
       * Reset the queue and stops processing.
       * @returns {void}
       * */
      resetQueue() {
        this.queue = [];
        this.isProcessing = false;
        this.clearCallback = null;
      },

      /**
       * Processes the queue by executing each asynchronous function in order.
       * It waits for each function to complete before moving to the next one.
       *
       * @returns {Promise<void>}
       */
      async processQueue() {
        this.isProcessing = true;

        while (this.queue.length > 0) {
          const currentFunc = this.queue.shift(); // Remove the first item from the queue
          try {
            await currentFunc(); // Execute the async function
          } catch (error) {
            console.error('Error executing function:', error);
          }
        }

        this.isProcessing = false;
        this.lastRun(); // Calls the lastRun function after finishing the queue
      },

      /**
       * Sets a callback function to be called after the queue has been cleared.
       * Can be used to perform additional actions after the queue has been processed.
       * The callback function should be a void function.
       * @param {Function} callback - The callback function to be called after the queue is cleared.
       * @returns {void}
       */
      setClearCallback(callback) {
        this.clearCallback = callback;
      },

      /**
       * Called after all functions in the queue have been executed.
       * @returns {void}
       */
      lastRun() {
        if (this.clearCallback) {
          this.clearCallback(); // Calls the callback function if it exists
        }
      },
    };
  }

  // StrikethroughManager object to handle strikethrough logic
  const StrikethroughManager = {
    /**
     * Initializes the StrikethroughManager by setting up event listeners and observers.
     */
    init() {
      this.queue = createQueue(); // Create a queue instance
      this.queue.setClearCallback(() => this.cleanupAllStrikethrough()); // Set the clear callback without publishing event to avoid infinite loop
      document.addEventListener('DOMContentLoaded', async () => {
        await this.cleanupAllStrikethrough(true); // Cleanup strikethrough items from the cart and publish event
        this.queue.addToQueue(() => this.applyStrikethrough()); // Add to queue on DOM load
        this.setupObserver();
      });
    },

    /**
     * Applies strikethrough to product cards.
     * If already processing, adds the function to the queue.
     * Handles errors and ensures proper cleanup.
     */
    async applyStrikethrough() {
      try {
        const productCards = this.getProductCards();
        if (!productCards.length) {
          return;
        }
        productCards.forEach((card) => card.classList.add('strikethrough-processing'));
        const variants = productCards.map((card) => card.getAttribute('data-strikethrough-id'));
        const variantsWithStrikethrough = await this.getStrikethroughPrices(variants);
        this.applyPricesToCards(productCards, variantsWithStrikethrough);
      } catch (error) {
        console.error('Error applying strikethrough:', error);
        this.disconnectObserver(); // Disconnect the observer on error
        // this.queue.resetQueue(); // Clear the queue on error
        await this.cleanupAllStrikethrough(true); // Cleanup strikethrough items from the cart and publish event
      }
    },

    /**
     * Retrieves product cards that need strikethrough.
     * @returns {Array} - An array of product card elements.
     */
    getProductCards() {
      return Array.from(document.querySelectorAll('[data-strikethrough-id]:not(.strikethrough-loaded)'));
    },

    /**
     * Applies prices with strikethrough to product cards.
     * @param {Array} productCards - An array of product card elements.
     * @param {Array} variantsWithStrikethrough - An array of variants with strikethrough data.
     */
    applyPricesToCards(productCards, variantsWithStrikethrough) {
      const dollarFormat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

      productCards.forEach((card) => {
        const variantId = card.getAttribute('data-strikethrough-id');
        if (card.classList.contains('strikethrough-loaded')) return;

        card.classList.add('strikethrough-loaded');
        card.classList.remove('strikethrough-processing');
        const variant = variantsWithStrikethrough.find((v) => v.id == variantId);

        if (variant) {
          if (card.id === 'DiscountedPriceOnSubscriptionWidget') {
            this.applySubscriptionWidgetStyle(card, dollarFormat.format(variant.discounted_price));
          } else {
            this.applyStrikethroughToCard(card, variant, dollarFormat);
          }
        }
      });
    },

    /**
     * Applies styles for the Subscription Widget.
     * @param {Element} card - The product card element.
     * @param {string} formattedPrice - The formatted discounted price.
     */
    applySubscriptionWidgetStyle(card, formattedPrice) {
      const style = document.createElement('style');
      style.id = 'subscription_widget_strikethrough';
      style.innerHTML = `
        .shopify_subscriptions_in_widget_price {
          text-decoration: line-through;
          font-weight: 400;
        }
        .shopify_subscriptions_in_widget_price::after {
          content: "${formattedPrice}";
          text-decoration: none !important;
          margin-left: 6px;
          display: inline-block;
          font-weight: bold;
        }
      `;
      document.body.appendChild(style);
    },

    /**
     * Applies strikethrough to a specific product card.
     * @param {Element} card - The product card element.
     * @param {Object} variant - The variant data containing pricing.
     * @param {Object} dollarFormat - The number formatting object for currency.
     */
    applyStrikethroughToCard(card, variant, dollarFormat) {
      const priceContainer = card.querySelector('.price__container');
      if (!priceContainer) return;

      priceContainer.classList.add('strikethrough');
      const discountedPrice = document.createElement('div');
      discountedPrice.classList.add('discounted-price');
      discountedPrice.textContent = dollarFormat.format(variant.discounted_price);
      priceContainer.appendChild(discountedPrice);
    },

    /**
     * Sets up a MutationObserver to watch for added nodes in the DOM.
     * Check if the added node is an element and should have strikethrough applied
     */
    setupObserver() {
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'childList') {
            let shouldAddToQueue = false; // Flag to determine if applyStrikethrough should be added to the queue
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (this.elementWithStrikethrough(node)) shouldAddToQueue = true; // Set flag to true if an target element is detected
              }
            });
            if (shouldAddToQueue) this.queue.addToQueue(() => this.applyStrikethrough());
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      this.observer = observer;
    },

    /**
     * Disconnects the MutationObserver.
     * Used to disconnect the observer when an error occurs.
     * This prevents the observer from running after an error.
     * @returns {void}
     * */
    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },

    /**
     * Checks if an element or its children have the strikethrough ID.
     * @param {Element} element - The element to check.
     * @returns {boolean} - True if the element has the data-set strikethrough ID, otherwise false.
     */
    elementWithStrikethrough(element) {
      if (element.hasAttribute('data-strikethrough-id') && !element.classList.contains('strikethrough-loaded')) {
        return true;
      }
      for (const child of element.children) {
        if (this.elementWithStrikethrough(child)) {
          return true;
        }
      }
      return false;
    },

    /**
     * Cleans up items with strikethrough from the cart.
     * @param {boolean} shouldPublish - Flag to determine if the cart update event should be published.
     */
    async cleanupAllStrikethrough(shouldPublish = false) {
      let updatedCart;
      try {
        const cart = await this.fetchCart();
        if (!cart || !cart.items.length) {
          updatedCart = cart;
          return;
        }
        const itemsWithStrikethrough = this.filterItemsWithStrikethrough(cart.items);
        if (!itemsWithStrikethrough.length) {
          console.log('No items with strikethrough present in cart');
          return;
        }
        const { cart: finalCart } = await this.bulkRemoveFromCart(itemsWithStrikethrough);
        updatedCart = finalCart;
        console.log('Strikethrough items removed from cart');
      } catch (error) {
        console.error('Error during cleanup:', error);
      } finally {
        if (!updatedCart || !shouldPublish) return;
        console.log('Publishing cart update event');
        console.log({ source: 'strikethrough', cartData: updatedCart });
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'strikethrough', cartData: updatedCart });
        publishEvent = false;
      }
    },

    /**
     * Filters items in the cart to find those with strikethrough.
     * @param {Array} items - The items in the cart.
     * @returns {Array} - An array of items with strikethrough.
     */
    filterItemsWithStrikethrough(items) {
      return items.filter((item) => item.properties['_strikethrough'] || item.properties['strikethrough']);
    },

    /**
     * Fetches strikethrough prices for the given variants.
     * @param {Array} variants - An array of variant IDs.
     * @returns {Promise<Array>} - A promise that resolves to an array of variants with strikethrough data.
     */
    async getStrikethroughPrices(variants) {
      try {
        const variantsWithStrikethrough = [];
        const variantsToCalculate = [];
        const strikethroughSession = JSON.parse(sessionStorage.getItem('strikethroughPrices')) || [];

        // Filter valid variants from session storage
        const validVariants = strikethroughSession.filter((v) => v.expires > Date.now());
        sessionStorage.setItem('strikethroughPrices', JSON.stringify(validVariants));

        variants.forEach((variantId) => {
          const sessionVariant = validVariants.find((variant) => variant.id == variantId);
          if (sessionVariant) {
            variantsWithStrikethrough.push(sessionVariant);
          } else {
            variantsToCalculate.push(variantId);
          }
        });

        // Fetch prices for variants that are not cached
        const uniqueVariantsToCalculate = [...new Set(variantsToCalculate)];
        if (uniqueVariantsToCalculate.length) {
          const calculatedVariants = await this.calculateDiscountedPrices(uniqueVariantsToCalculate);
          variantsWithStrikethrough.push(...calculatedVariants);
        }

        sessionStorage.setItem('strikethroughPrices', JSON.stringify(variantsWithStrikethrough));

        // Ensure the return value is filtered correctly
        return variantsWithStrikethrough.filter((v) => v.discounted_price !== v.original_price);
      } catch (error) {
        console.error('Error getting strikethrough prices:', error);
        throw new Error(error.message);
      }
    },

    /**
     * Calculates discounted prices for the provided unique variant IDs.
     * @param {Array} uniqueVariantsToCalculate - An array of unique variant IDs.
     * @returns {Promise<Array>} - A promise that resolves to an array of line items with discounted prices.
     */
    async calculateDiscountedPrices(uniqueVariantsToCalculate) {
      try {
        const { cartJSON, addedLineItems } = await this.bulkAddToCart(uniqueVariantsToCalculate);
        const { removedLineItems } = await this.bulkRemoveFromCart(addedLineItems);

        if (addedLineItems.length !== removedLineItems.length || !cartJSON) {
          throw new Error('Error calculating strikethrough prices');
        }

        // Get only the items with strikethrough property
        return cartJSON.items
          .filter((item) => item.properties['_strikethrough'])
          .map((line_item) => ({
            id: line_item.id,
            original_price: parseFloat(line_item.original_price / 100),
            discounted_price: parseFloat(line_item.final_price / 100),
            expires: Date.now() + 1000 * 60 * 60 * 1, // 1 hour
          }));
      } catch (error) {
        console.error('Error calculating discounted prices:', error);
        throw new Error(error.message);
      }
    },

    /**
     * Fetches the current cart data from the server.
     * @returns {Promise<Object>} - A promise that resolves to the cart data.
     */
    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
        return await res.json();
      } catch (error) {
        console.error('Error while fetching the cart:', error);
        throw new Error(error.message);
      }
    },

    /**
     * Bulk adds items to the cart for processing.
     * @param {Array} variants - An array of variant IDs to add to the cart.
     * @returns {Promise<Object>} - A promise that resolves to the added line items and cart JSON.
     */
    async bulkAddToCart(variants) {
      const cartSectionHandle = 'section-cart-object';

      try {
        const body = JSON.stringify({
          items: variants.map((variant) => ({
            id: variant,
            quantity: 1,
            properties: { _strikethrough: true },
          })),
          sections: cartSectionHandle,
        });

        const response = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
        const data = await response.json();
        let cartSectionHtml = data.sections?.[cartSectionHandle] || null;

        if (!cartSectionHtml) {
          const res = await fetch(`/?section_id=${cartSectionHandle}`);
          cartSectionHtml = (await res.text()) || null;
        }

        if (!cartSectionHtml) throw new Error('Not possible to fetch cart section');

        const html = new DOMParser().parseFromString(cartSectionHtml, 'text/html');
        const cartJSON = JSON.parse(html.querySelector('[type="application/json"]').textContent);

        if (!cartJSON.items || !cartJSON.items.length) {
          throw new Error('Error fetching the cart section');
        }

        return { addedLineItems: data.items, cartJSON };
      } catch (error) {
        console.error('Error adding items to cart:', error);
        throw new Error(error.message);
      }
    },

    /**
     * Bulk removes items from the cart.
     * @param {Array} lineItemsToRemove - An array of line items to remove.
     * @returns {Promise<Object>} - A promise that resolves to the removed line items and updated cart data.
     */
    async bulkRemoveFromCart(lineItemsToRemove) {
      try {
        const updates = {};
        lineItemsToRemove.forEach((line_item) => {
          updates[line_item.key] = 0;
        });

        const body = JSON.stringify({ updates });
        const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body });
        const data = await response.json();

        const removedLineItems = lineItemsToRemove.filter(
          (line_item) => !data.items.find((item) => item.key === line_item.key)
        );

        return { removedLineItems, cart: data };
      } catch (error) {
        console.error('Error removing items from cart:', error);
        /**
         * Attempt to remove items individually if bulk removal fails
         * This is a fallback mechanism to ensure items are removed from the cart
         * */
        try {
          for (const item of lineItemsToRemove) {
            const res = await fetch(`/cart/change.js?id=${item.key}&quantity=0`);
            if (!res.ok) {
              throw new Error(`Error removing Strikethrough item [${item.key}] from cart`);
            }
          }
          console.log('Error handled and Strikethrough items removed individually');
          throw new Error('Error handled');
        } catch (err) {
          console.error('Error removing items from cart:', err);
          throw new Error(err.message);
        }
      }
    },
  };

  // Initialize the StrikethroughManager
  StrikethroughManager.init();
})();
